import moment, { MomentInput } from 'moment';
import { DependentTask } from 'api/tasks';
import { formatDate } from 'components/organisms/projects/project-plan/timeline/helpers';
import {
  ProjectTemplateProjectPhase,
  ProjectTemplateTask,
} from 'data/templates/projects';
import {
  DateShiftUpdates,
  DateShiftUpdatesForPhase,
  DateShiftUpdatesForTask,
  Entity,
  EntityDateShiftUpdate,
  EntityType,
  List,
} from './date-shift';

type TemplateEntity = Entity & {
  duration: number;
};

type TemplateList = {
  [id: number]: TemplateEntity;
};

export const getPhaseList = (
  phases: ProjectTemplateProjectPhase[],
): TemplateList => {
  const result: TemplateList = {};

  phases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDay,
      duration,
      taskOrder,
      tasks,
      dependentProjectPhases,
    }) => {
      result[projectPhaseId] = {
        depth: 0,
        id: projectPhaseId,
        name: projectPhaseName,
        type: EntityType.Phase,
        startDate: formatDate(moment().add(startDay, 'day')),
        dueDate: formatDate(moment().add(startDay + duration - 1, 'day')),
        duration,
        children:
          taskOrder?.slice(0, -1) ??
          tasks.slice(0, -1).map((_task) => _task.taskId ?? _task),
        dependentOn: dependentProjectPhases?.map(
          ({ projectPhaseId: dPhaseId }) => dPhaseId,
        ),
      };

      dependentProjectPhases?.forEach(({ projectPhaseId: dPhaseId }) => {
        result[dPhaseId] = {
          ...(result[dPhaseId] ?? {}),
          dependencyFor: [
            ...(result[dPhaseId]?.dependencyFor ?? []),
            projectPhaseId,
          ],
        };
      });
    },
  );

  return result;
};

export const getTaskList = (tasks: ProjectTemplateTask[]): TemplateList => {
  const result: TemplateList = {};

  tasks
    .filter(({ isNew }) => !isNew)
    .forEach(
      ({
        taskId,
        startDay,
        duration,
        subTasks,
        dependentTasks,
        parentTask,
        projectPhase,
        taskName,
      }) => {
        result[taskId] = {
          ...(result[taskId] ?? {}),
          depth: 1, // initial value
          name: taskName,
          id: taskId,
          type: EntityType.Task,
          startDate: formatDate(moment().add(startDay, 'day')),
          dueDate: formatDate(moment().add(startDay + duration - 1, 'day')),
          duration,
          children: subTasks.map((_task) => _task?.taskId ?? _task),
          dependentOn: dependentTasks?.map(({ taskId: dTaskId }) => dTaskId),
          parent: parentTask?.taskId ?? projectPhase?.projectPhaseId,
        };

        dependentTasks?.forEach(({ taskId: dTaskId }) => {
          result[dTaskId] = {
            ...(result[dTaskId] ?? {}),
            dependencyFor: [...(result[dTaskId]?.dependencyFor ?? []), taskId],
          };
        });
      },
    );

  const getDepth = (id) => {
    return !result[id]?.parent ? 0 : getDepth(result[id].parent) + 1;
  };

  Object.values(result).forEach((_task) => {
    result[_task.id] = {
      ..._task,
      depth: getDepth(_task.id),
    };
  });

  return result;
};

export const getTemplatePhaseUpdates = (
  list: DateShiftUpdates,
): DateShiftUpdatesForPhase[] => {
  const updates: DateShiftUpdatesForPhase[] = [];

  Object.values(list).forEach(({ id, startDate, dueDate }) => {
    updates.push({
      projectPhaseId: id,
      startDate: startDate ? moment(startDate).format() : undefined,
      dueDate: dueDate ? moment(dueDate).format() : undefined,
    });
  });

  return updates;
};

export const getTemplateTaskUpdates = (
  updateList: DateShiftUpdates,
  taskList: List,
): DateShiftUpdatesForTask[] => {
  const updates: DateShiftUpdatesForTask[] = [];

  Object.values(updateList).forEach(
    ({ id, startDate, dueDate, dependentOn }) => {
      updates.push({
        taskId: id,
        startDate: startDate ? moment(startDate).format() : undefined,
        dueDate: dueDate ? moment(dueDate).format() : undefined,
        dependentTasks: dependentOn?.map(
          (_taskId) =>
            ({
              taskId: _taskId,
              taskName: taskList[_taskId].name,
            } as DependentTask),
        ),
      });
    },
  );

  return updates;
};

export const isValidDateShift = (phaseUpdatesList: DateShiftUpdates) => {
  const earliestDate: MomentInput = Object.values(phaseUpdatesList).reduce(
    (prev: MomentInput, update: EntityDateShiftUpdate): MomentInput =>
      prev === null ||
      (update.startDate && moment(update.startDate).isBefore(moment(prev)))
        ? update.startDate
        : prev,
    null,
  );

  if (earliestDate === null) {
    return true;
  }

  return moment().startOf('day').diff(moment(earliestDate), 'day', true) < 0;
};
